/**
 * Referral Link Utilities
 *
 * Phase 4.2 - Shareable Referral Links
 *
 * Handles:
 * - Capturing ?ref= parameter from URL
 * - Persisting referral code in localStorage
 * - Retrieving stored referral code for checkout
 *
 * Usage:
 * 1. On landing pages: Call captureReferralFromURL() to store any ?ref= param
 * 2. At checkout: Call getStoredReferralCode() to retrieve the code
 * 3. After checkout: Call clearStoredReferralCode() to clean up
 */

import { captureClientError } from '@/lib/error-reporting/client';

// BUG FIX: Export these constants so other files can import them
// This prevents hardcoded values getting out of sync
export const REFERRAL_STORAGE_KEY = 'cmg_referral_code';
export const REFERRAL_TIMESTAMP_KEY = 'cmg_referral_timestamp';

// Server-side capture key: first-party cookie readable in /api/auth/callback.
// Covers email-verify AND Google OAuth signups (both end in a top-level GET
// navigation to cmgworkflow.com — SameSite=Lax is sent on that navigation).
export const REFERRAL_COOKIE_KEY = 'cmg_ref';

// Referral codes expire after 30 days
const REFERRAL_EXPIRY_DAYS = 30;

// BUG FIX: Max referral code length to prevent localStorage DoS attacks
const MAX_REFERRAL_CODE_LENGTH = 50;

// BUG FIX #21 & #23: Valid referral code pattern (alphanumeric, hyphens, underscores only)
// Must match the pattern in track-click/route.ts: /^[a-zA-Z0-9_-]+$/
const VALID_REFERRAL_CODE_PATTERN = /^[A-Z0-9_-]+$/;

/**
 * Capture referral code from URL and store in localStorage
 * Call this on landing pages (signup, login, homepage, etc.)
 *
 * @returns The captured referral code, or null if none present
 */
export function captureReferralFromURL(): string | null {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');

  if (refCode) {
    // BUG FIX #21: URL decoding happens automatically via URLSearchParams,
    // but we sanitize after trimming and uppercasing
    const trimmedCode = refCode.trim().toUpperCase();

    // BUG FIX: Reject codes that are too long (potential attack vector)
    if (trimmedCode.length === 0 || trimmedCode.length > MAX_REFERRAL_CODE_LENGTH) {
      // Invalid code length - clean URL but don't store
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
      return null;
    }

    // BUG FIX #21 & #23: Validate code contains only allowed characters
    // This prevents XSS/injection via crafted referral codes
    if (!VALID_REFERRAL_CODE_PATTERN.test(trimmedCode)) {
      // Invalid characters - clean URL but don't store
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('ref');
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        captureClientError(error, 'client_runtime', {
          component: 'referral-link.captureReferralFromURL',
          action: 'clean_url_invalid_code',
        }).catch(() => {});
      }
      return null;
    }

    // Store the referral code and timestamp
    let storedSuccessfully = false;
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, trimmedCode);
      localStorage.setItem(REFERRAL_TIMESTAMP_KEY, Date.now().toString());
      storedSuccessfully = true;
    } catch (error) {
      captureClientError(error, 'client_runtime', {
        component: 'referral-link.captureReferralFromURL',
        action: 'localStorage_write',
      }).catch(() => {});
      // BUG FIX: Don't return code if localStorage failed - it won't persist
      // Continue to clean URL but don't return the code
    }

    // Server-side signal for signup-time referral linking (read in /api/auth/callback).
    // Independent of localStorage so it survives private mode. SameSite=Lax is sent on the
    // top-level GET navigation to the callback (email-verify + OAuth both end there).
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${REFERRAL_COOKIE_KEY}=${encodeURIComponent(trimmedCode)}; path=/; max-age=${REFERRAL_EXPIRY_DAYS * 24 * 60 * 60}; SameSite=Lax${secure}`;
    } catch {
      /* non-fatal */
    }

    // Clean the URL without reloading (remove ?ref= param)
    // intentional: window.location.href is always a valid URL (browser-guaranteed)
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());

    // BUG FIX: Only return code if it was successfully stored
    return storedSuccessfully ? trimmedCode : null;
  }

  return null;
}

/**
 * Get stored referral code from localStorage
 * Returns null if no code stored or if code has expired
 *
 * @returns The stored referral code, or null
 */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const timestamp = localStorage.getItem(REFERRAL_TIMESTAMP_KEY);

    if (!code || !timestamp) return null;

    // BUG FIX #40: Validate localStorage data wasn't tampered with
    // Re-validate the code format in case user modified localStorage
    if (code.length > MAX_REFERRAL_CODE_LENGTH || !VALID_REFERRAL_CODE_PATTERN.test(code)) {
      // Tampered or invalid - clear and return null
      clearStoredReferralCode();
      return null;
    }

    // Check if expired
    const storedTime = parseInt(timestamp, 10);
    if (Number.isNaN(storedTime)) {
      clearStoredReferralCode();
      return null;
    }

    // BUG FIX #40: Validate timestamp is a reasonable number (not tampered)
    // Must be a positive number, not in the future, and within last year
    const now = Date.now();
    const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000;
    if (storedTime < oneYearAgo || storedTime > now) {
      // Invalid timestamp - clear and return null
      clearStoredReferralCode();
      return null;
    }

    const expiryTime = storedTime + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    if (Date.now() > expiryTime) {
      // Expired - clear storage
      clearStoredReferralCode();
      return null;
    }

    return code;
  } catch (error) {
    captureClientError(error, 'client_runtime', {
      component: 'referral-link.getStoredReferralCode',
      action: 'localStorage_read',
    }).catch(() => {});
    return null;
  }
}

/**
 * Clear stored referral code
 * Call after successful checkout to prevent reuse
 */
export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    localStorage.removeItem(REFERRAL_TIMESTAMP_KEY);
  } catch (error) {
    captureClientError(error, 'client_runtime', {
      component: 'referral-link.clearStoredReferralCode',
      action: 'localStorage_remove',
    }).catch(() => {});
  }
  try {
    // Match the Secure flag used at write time so the cookie clears on HTTPS (FIX 5).
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${REFERRAL_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax${secure}`;
  } catch {
    /* non-fatal */
  }
}

/**
 * Check if a referral code is currently stored
 */
export function hasStoredReferralCode(): boolean {
  return getStoredReferralCode() !== null;
}

/**
 * Generate a shareable referral link
 *
 * @param code The referral code
 * @param baseUrl Optional base URL (defaults to current origin)
 * @returns The full shareable URL
 */
export function generateReferralLink(code: string, baseUrl?: string): string {
  const base = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/auth/register?ref=${code}`;
}
